/**
 * Customers list — S09. Full prototype layout with V1.0.x trims.
 *
 * Surfaces (top → bottom), per `prototype/sales/customers.html`:
 *
 *   1. Page head: H1, lead summary, [Import CSV] [+ Add customer]
 *   2. KPI strip: Total / New this month / Avg payment time / Top exposure
 *   3. Tabs: All · With overdue · Active · 30d · New customers · Archived
 *   4. Toolbar: search + Health filter + Sort + view-toggle (list/grid) + Export
 *   5. List of customer rows OR grid of cards (driven by the view-toggle)
 *   6. Footer summary strip inside the list card (totals, best/top exposure)
 *
 * Every UI affordance the prototype shows is present here, even when the
 * data backing it isn't wired yet. Stub items are visibly muted with `title`
 * tooltips that say "wires up with X" — keeps the IA visible without lying.
 *
 * Data we don't have today (with `// TODO V1.0.x` markers below):
 *   - Lead summary outstanding/overdue/YTD → needs invoices
 *   - Health filter pill, exposure sort, Export, Import CSV → UI stubs
 *   - Footer "Outstanding / Best payer / Top exposure" → needs invoices
 *
 * @see prototype/sales/customers.html — V1.1 Studio Slate baseline
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ChevronDown,
  Download,
  HeartPulse,
  Import,
  LayoutGrid,
  List as ListIcon,
  Plus,
  Search as SearchIcon,
  SlidersHorizontal,
} from "lucide-react";
import type { customers as customersContract } from "@flint/api-contract";
import type { z } from "zod";
import {
  Alert,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  TabsRoot,
  TabsList,
  TabsTrigger,
  ToggleGroupRoot,
  ToggleGroupItem,
} from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { paiseToInrShort } from "../../../../lib/format.ts";
import { usePermission } from "../../../../lib/permissions.ts";
import { MasterListCard, ListPagination } from "../../../../features/master-list/index.ts";
import {
  CustomerCard,
  CustomerRow,
  EmptyState,
  KpiStrip,
  exportCustomersXlsx,
  ImportCustomersDialog,
} from "../../../../features/customers/index.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";

type ListRow = z.infer<typeof customersContract.listRow>;

/**
 * Search-param shape. Each field is `T | undefined` (not just `T?`) so
 * `Partial<>` patches can carry explicit-`undefined` values — needed
 * because we use `undefined` as the "clear this param" sentinel when
 * stripping a key from the URL.
 */
interface CustomersSearch {
  q?: string | undefined;
  tab?: "all" | "overdue" | "active" | "new" | "archived" | undefined;
  view?: "list" | "grid" | undefined;
  health?: "all" | "late" | "slow" | "on-time" | undefined;
  sort?: "recent" | "name" | "exposure" | undefined;
}

const TABS: Array<{ id: NonNullable<CustomersSearch["tab"]>; label: string }> = [
  { id: "all", label: "All" },
  { id: "overdue", label: "With overdue" },
  { id: "active", label: "Active · 30d" },
  { id: "new", label: "New customers" },
  { id: "archived", label: "Archived" },
];

export const Route = createFileRoute("/(workspace)/sales/customers/")({
  component: CustomersListPage,
  validateSearch: (s: Record<string, unknown>): CustomersSearch => {
    const out: CustomersSearch = {};
    if (typeof s.q === "string" && s.q) out.q = s.q;
    if (s.tab && TABS.some((t) => t.id === s.tab)) {
      out.tab = s.tab as CustomersSearch["tab"];
    }
    if (s.view === "grid" || s.view === "list") out.view = s.view;
    if (
      s.health === "late" ||
      s.health === "slow" ||
      s.health === "on-time" ||
      s.health === "all"
    ) {
      out.health = s.health;
    }
    if (s.sort === "name" || s.sort === "exposure" || s.sort === "recent") out.sort = s.sort;
    return out;
  },
});

function CustomersListPage() {
  const canManage = usePermission("customers.manage");
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [draftQuery, setDraftQuery] = useState(search.q ?? "");
  const [importOpen, setImportOpen] = useState(false);
  // `effectiveQuery` is the debounced value that drives the tRPC query. We
  // deliberately keep typing out of the URL so it doesn't churn history;
  // Enter (submitSearch) and Clear remain the explicit URL-write paths.
  const [effectiveQuery, setEffectiveQuery] = useState(search.q ?? "");

  const tab = search.tab ?? "all";
  const view = search.view ?? "list";
  const health = search.health ?? "all";
  const sort = search.sort ?? "recent";

  // Cursor-stack pagination (the shared list pattern — see vendors list).
  const PAGE_SIZE = 50;
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1];
  useEffect(() => {
    setCursorStack([]);
  }, [tab, effectiveQuery]);

  const list = trpc.customers.list.useQuery(
    {
      limit: PAGE_SIZE,
      tab,
      ...(effectiveQuery ? { q: effectiveQuery } : {}),
      ...(cursor !== undefined ? { cursor } : {}),
    },
    { placeholderData: (prev) => prev },
  );

  const counts = trpc.customers.counts.useQuery();
  const exportMutation = trpc.customers.export.useMutation();

  const updateSearch = (patch: Partial<CustomersSearch>) => {
    const next: CustomersSearch = { ...search, ...patch };
    if (!next.q) delete next.q;
    if (next.tab === "all") delete next.tab;
    if (next.view === "list") delete next.view;
    if (next.health === "all") delete next.health;
    if (next.sort === "recent") delete next.sort;
    void navigate({ to: "/sales/customers", search: next });
  };

  useEffect(() => {
    const next = draftQuery.trim();
    if (next === effectiveQuery) return;
    const t = setTimeout(() => setEffectiveQuery(next), 300);
    return () => clearTimeout(t);
  }, [draftQuery, effectiveQuery]);

  // Reflect external URL changes (Clear, deep link, back/forward) into
  // both the input and the effective query.
  useEffect(() => {
    const urlQ = search.q ?? "";
    if (urlQ !== draftQuery.trim()) setDraftQuery(urlQ);
    setEffectiveQuery(urlQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.q]);

  const submitSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateSearch({ q: draftQuery.trim() || undefined });
  };

  const items = (list.data?.items as ListRow[] | undefined) ?? [];
  const sortedItems = sortRows(items, sort);
  // Page-scoped derivations — describe what's currently loaded (KPI strip +
  // footer summary). Header totals come from `counts.data` (AC8).
  const topExposurePaise = maxPaise(items, (row) => row.outstandingPaise);
  const active30dCount = items.filter((row) => isWithinLastDays(row.lastActivityAt, 30)).length;

  // Pagination derivations — page index = cursor-stack depth + 1.
  const pageNumber = cursorStack.length + 1;
  const showingFrom = items.length === 0 ? 0 : cursorStack.length * PAGE_SIZE + 1;
  const showingTo = cursorStack.length * PAGE_SIZE + items.length;
  const totalRows = tab === "archived" ? counts.data?.archived : counts.data?.total;
  const handlePrev = () => setCursorStack((s) => s.slice(0, -1));
  const handleNext = () => {
    const next = list.data?.nextCursor;
    if (next) setCursorStack((s) => [...s, next]);
  };

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Sales", to: "/" }, { label: "Customers" }]}>
      <div className="space-y-[18px]">
        {/* ── Page head ───────────────────────────────────────────── */}
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-ink tracking-tight leading-tight">Customers</h1>
            <p className="text-sm text-muted mt-1.5 flex items-center gap-x-2.5 gap-y-1 flex-wrap">
              {counts.data ? (
                <>
                  <span>
                    <strong className="num font-bold text-ink">{counts.data.total}</strong>{" "}
                    customers
                  </span>
                  <Dot />
                  <span className="text-muted/80">
                    <strong className="num">
                      {paiseToInrShort(counts.data.outstandingPaise)}
                    </strong>{" "}
                    outstanding
                  </span>
                  <Dot />
                  <span
                    className={
                      counts.data.overdueCustomerCount > 0 ? "text-bad" : "text-muted/80"
                    }
                  >
                    <strong className="num">{counts.data.overdueCustomerCount}</strong> with
                    overdue
                  </span>
                  <Dot />
                  <span className="text-muted/80">
                    <strong className="num">{paiseToInrShort(counts.data.ytdInvoicedPaise)}</strong>{" "}
                    invoiced
                    YTD
                  </span>
                </>
              ) : (
                "Loading…"
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {canManage && (
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                title="Import customers from a CSV file"
                className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm border border-line bg-paper text-xs font-semibold text-ink hover:bg-bg"
              >
                <Import size={13} strokeWidth={2} aria-hidden />
                Import CSV
              </button>
            )}
            {canManage && (
              <Link
                to="/sales/customers/new"
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm bg-ink text-paper text-sm font-semibold hover:bg-ink-2"
              >
                <Plus size={13} strokeWidth={2.4} aria-hidden />
                Add customer
              </Link>
            )}
          </div>
        </header>

        <ImportCustomersDialog open={importOpen} onOpenChange={setImportOpen} />

        {/* ── KPI strip ───────────────────────────────────────────── */}
        <KpiStrip
          total={counts.data?.total ?? 0}
          archived={counts.data?.archived ?? 0}
          newThisMonth={counts.data?.newThisMonth ?? 0}
          topExposurePaise={topExposurePaise.toString()}
        />

        {/* ── Tabs ────────────────────────────────────────────────── */}
        <TabsRoot
          value={tab}
          onValueChange={(v) => updateSearch({ tab: v as CustomersSearch["tab"] })}
        >
          <TabsList aria-label="Customer status">
            {TABS.map((t) => {
              const tabCount =
                t.id === "all"
                  ? counts.data?.total
                  : t.id === "archived"
                    ? counts.data?.archived
                    : t.id === "new"
                      ? counts.data?.newThisMonth
                      : t.id === "active"
                        ? active30dCount
                        : (counts.data?.overdueCustomerCount ?? 0);
              return (
                <TabsTrigger
                  key={t.id}
                  value={t.id}
                  count={tabCount}
                  countTone={t.id === "overdue" ? "warn" : "default"}
                >
                  {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </TabsRoot>

        {/* ── Toolbar ─────────────────────────────────────────────── */}
        <div className="flex items-center gap-2 flex-wrap">
          <form onSubmit={submitSearch} className="flex-1 min-w-[260px] max-w-[380px]">
            <label className="flex items-center gap-2 h-9 px-3 rounded-sm border border-line bg-bg focus-within:border-sky-600 focus-within:bg-paper focus-within:ring-2 focus-within:ring-sky-100 transition-colors">
              <SearchIcon size={14} strokeWidth={2} aria-hidden className="text-muted shrink-0" />
              <input
                type="search"
                placeholder="Search by name, GSTIN, contact…"
                value={draftQuery}
                onChange={(e) => setDraftQuery(e.target.value)}
                className="flex-1 min-w-0 bg-transparent border-0 outline-0 text-sm placeholder:text-muted text-ink"
                aria-label="Search customers"
              />
              {draftQuery ? (
                <button
                  type="button"
                  onClick={() => {
                    setDraftQuery("");
                    setEffectiveQuery("");
                    if (search.q) updateSearch({ q: undefined });
                  }}
                  className="text-xs text-muted hover:text-ink"
                  aria-label="Clear search"
                >
                  clear
                </button>
              ) : null}
            </label>
          </form>

          {/* Health filter — TODO V1.0.x: wires to invoices-derived health buckets. */}
          <DropdownMenuRoot>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-line bg-paper text-xs font-semibold text-ink-2 hover:bg-bg hover:border-line-2"
              >
                <HeartPulse size={12} strokeWidth={2} aria-hidden className="text-muted" />
                Health
                <ChevronDown size={12} strokeWidth={2} aria-hidden className="text-muted" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>Filter by health</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={health}
                onValueChange={(v) => updateSearch({ health: v as CustomersSearch["health"] })}
              >
                <DropdownMenuRadioItem value="all">All customers</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="late" disabled>
                  Late · wires up next
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="slow" disabled>
                  Slow · wires up next
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="on-time" disabled>
                  On time · wires up next
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenuRoot>

          {/* Sort — recent, name, and invoice exposure. */}
          <DropdownMenuRoot>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-line bg-paper text-xs font-semibold text-ink-2 hover:bg-bg hover:border-line-2"
              >
                <SlidersHorizontal size={12} strokeWidth={2} aria-hidden className="text-muted" />
                <span className="font-medium text-muted">Sort ·</span>
                <span>{sortLabel(sort)}</span>
                <ChevronDown size={12} strokeWidth={2} aria-hidden className="text-muted" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>Sort customers</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={sort}
                onValueChange={(v) => updateSearch({ sort: v as CustomersSearch["sort"] })}
              >
                <DropdownMenuRadioItem value="recent">Most recently added</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="name">Name (A → Z)</DropdownMenuRadioItem>
                <DropdownMenuSeparator />
                <DropdownMenuRadioItem value="exposure">Biggest exposure</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenuRoot>

          <span aria-hidden className="hidden sm:block flex-1" />

          {/* View toggle — hidden on phone where rows + cards look the same
              after the row collapses to avatar+name+chev anyway. */}
          <ToggleGroupRoot
            type="single"
            value={view}
            onValueChange={(v) => {
              if (v) updateSearch({ view: v as CustomersSearch["view"] });
            }}
            aria-label="View mode"
            className="hidden sm:inline-flex"
          >
            <ToggleGroupItem value="list" aria-label="List view">
              <ListIcon size={14} strokeWidth={2} aria-hidden />
            </ToggleGroupItem>
            <ToggleGroupItem value="grid" aria-label="Grid view">
              <LayoutGrid size={14} strokeWidth={2} aria-hidden />
            </ToggleGroupItem>
          </ToggleGroupRoot>

          {/* Export XLSX — dumps the current list (respects tab / search / sort)
              as a styled Excel workbook so phones / GSTINs don't render as
              scientific notation and money columns stay numeric. Hidden on
              phone to free toolbar space; accountants export from desktop. */}
          <button
            type="button"
            disabled={exportMutation.isPending}
            title={
              exportMutation.isPending
                ? "Preparing your download…"
                : "Download the current customers list as an Excel file"
            }
            onClick={() => {
              exportMutation.mutate(
                {
                  tab,
                  sort,
                  ...(effectiveQuery ? { q: effectiveQuery } : {}),
                },
                {
                  onSuccess: (payload) => {
                    void exportCustomersXlsx(payload, {
                      tab,
                      sort,
                      ...(effectiveQuery ? { q: effectiveQuery } : {}),
                    });
                  },
                },
              );
            }}
            className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-line bg-paper text-xs font-semibold text-ink hover:bg-bg disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Download size={12} strokeWidth={2} aria-hidden />
            {exportMutation.isPending ? "Exporting…" : "Export"}
          </button>
        </div>

        {/* ── Body ────────────────────────────────────────────────── */}
        {list.isLoading ? (
          <p className="text-muted">Loading customers…</p>
        ) : list.error ? (
          <Alert intent="bad" title="Couldn't load customers" description={list.error.message} />
        ) : sortedItems.length > 0 ? (
          view === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {sortedItems.map((c) => (
                <CustomerCard key={c.id} customer={c} />
              ))}
            </div>
          ) : (
            <MasterListCard
              header={
                // Aligns with CustomerRow's `lg` grid template + padding.
                <div className="grid items-center gap-4 px-5 py-2.5 grid-cols-[44px_minmax(0,1.8fr)_minmax(0,1.2fr)_110px_110px_auto]">
                  <span aria-hidden />
                  <span>Customer</span>
                  <span>Activity</span>
                  <span className="text-right">Outstanding</span>
                  <span className="text-right">Billed FY</span>
                  <span aria-hidden />
                </div>
              }
              footer={[
                { label: "Total customers", value: counts.data?.total ?? 0 },
                { label: "Active 30d", value: counts.data?.newThisMonth ?? 0 },
                {
                  label: "Outstanding",
                  value: paiseToInrShort(counts.data?.outstandingPaise ?? "0"),
                  accent:
                    (counts.data?.outstandingPaise ?? "0") !== "0" ? "bad" : "default",
                },
                // TODO V1.0.x — best payer + top exposure from invoices.
                { label: "Best payer", value: "—" },
                { label: "Top exposure", value: paiseToInrShort(topExposurePaise) },
              ]}
              pagination={
                <ListPagination
                  pageNumber={pageNumber}
                  showingFrom={showingFrom}
                  showingTo={showingTo}
                  {...(typeof totalRows === "number" ? { total: totalRows } : {})}
                  sortLabel={`sorted by ${sortLabel(sort)}`}
                  hasPrev={cursorStack.length > 0}
                  hasNext={!!list.data?.nextCursor}
                  onPrev={handlePrev}
                  onNext={handleNext}
                />
              }
            >
              {sortedItems.map((c) => (
                <CustomerRow key={c.id} customer={c} />
              ))}
            </MasterListCard>
          )
        ) : effectiveQuery || search.q ? (
          <EmptyState variant="no-search-match" query={effectiveQuery || search.q!} />
        ) : tab === "archived" ? (
          <EmptyState variant="no-archived" />
        ) : tab === "overdue" || tab === "active" || tab === "new" ? (
          <EmptyState variant="no-tab-match" tab={tab} />
        ) : (
          <EmptyState variant="first-run" />
        )}

      </div>
    </WorkspaceShell>
  );
}

// ── helpers ──────────────────────────────────────────────────────

function Dot() {
  return <span aria-hidden className="h-[3px] w-[3px] rounded-full bg-line-2 shrink-0" />;
}

function sortLabel(s: NonNullable<CustomersSearch["sort"]>): string {
  switch (s) {
    case "name":
      return "A → Z";
    case "exposure":
      return "biggest exposure first";
    default:
      return "most recent";
  }
}

/**
 * Client-side reorder for sort options we can satisfy from the master row
 * alone. `recent` is the server's natural order (newest first by
 * `created_at` per the cursor-paginated query); `name` is alphabetical;
 * `exposure` is gated behind invoices and disabled in the dropdown.
 *
 * Cheap because page size is 50 — when the dataset grows, this moves
 * server-side via `listInput.sort`.
 */
function sortRows(rows: ListRow[], sort: NonNullable<CustomersSearch["sort"]>): ListRow[] {
  if (sort === "name") {
    return [...rows].sort((a, b) => a.name.localeCompare(b.name, "en-IN"));
  }
  if (sort === "exposure") {
    return [...rows].sort((a, b) => {
      const left = BigInt(a.outstandingPaise);
      const right = BigInt(b.outstandingPaise);
      return right > left ? 1 : right < left ? -1 : 0;
    });
  }
  return rows;
}

function maxPaise(rows: ListRow[], pick: (row: ListRow) => string): bigint {
  return rows.reduce((max, row) => {
    const value = BigInt(pick(row));
    return value > max ? value : max;
  }, 0n);
}

function isWithinLastDays(value: string | null, days: number): boolean {
  if (!value) return false;
  const at = new Date(value).getTime();
  return Number.isFinite(at) && Date.now() - at <= days * 24 * 60 * 60 * 1000;
}
